// frontend/src/pages/settings/users-roles/RolesTab.tsx
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { rbacApi } from './api';
import { RoleList } from './RoleList';
import { RoleEditor } from './RoleEditor';
import type { RoleDetail, RoleSummary } from './types';

export function RolesTab() {
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [selected, setSelected] = useState<RoleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    const res = await rbacApi.listRoles();
    if (res.success && res.data) { setRoles(res.data); setError(null); return res.data; }
    setError(res.error || 'No se pudieron cargar los roles');
    return [];
  }, []);

  const select = useCallback(async (id: number) => {
    const res = await rbacApi.getRole(id);
    if (res.success && res.data) { setSelected(res.data); setError(null); }
    else setError(res.error || 'No se pudo cargar el rol');
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const list = await loadList();
      if (list.length) await select(list[0].id);
      setLoading(false);
    })();
  }, [loadList, select]);

  const onNew = async () => {
    const name = window.prompt('Nombre del rol nuevo');
    if (!name) return;
    const res = await rbacApi.createRole({ name });
    if (res.success && res.data) { await loadList(); setSelected(res.data); }
    else setError(res.error || 'No se pudo crear el rol');
  };

  if (loading) return <div className="flex justify-center py-8 text-[var(--color-text-muted)]"><RefreshCw className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      {error && <div className="px-3 py-2 rounded-lg text-xs bg-red-500/10 border border-red-500/30 text-red-600">{error}</div>}
      <div className="flex flex-col lg:flex-row gap-4">
        <RoleList roles={roles} selectedId={selected?.id ?? null} onSelect={select} onNew={onNew} />
        {selected && (
          <RoleEditor
            key={selected.id}
            role={selected}
            onSaved={async (r) => { setSelected(r); await loadList(); }}
            onDeleted={async () => { const list = await loadList(); setSelected(null); if (list.length) await select(list[0].id); }}
            onDuplicate={async (r) => { await loadList(); setSelected(r); }}
          />
        )}
      </div>
    </div>
  );
}
